import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { 
  MapPin, Maximize, Euro, TrendingUp, Home, Building2, 
  LandPlot, Phone, ArrowRight, Badge, Banknote, PiggyBank,
  CheckCircle, Calculator, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FAVORITES_KEY = 'gdivision_favorites';

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [favorites, setFavorites] = useState(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const toggleFavorite = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(FAVORITES_KEY, JSON.stringify([...next]));
      } catch (_) { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get(`${API}/properties?published_only=true`);
        setProperties(response.data);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Appartement': return Building2;
      case 'Maison': return Home;
      case 'Terrain': return LandPlot;
      default: return Building2;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Disponible': return 'bg-green-500';
      case 'Sous compromis': return 'bg-orange-500';
      case 'Vendu': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const baseFiltered = filter === 'all' 
    ? properties 
    : properties.filter(p => p.property_type === filter);
  const filteredProperties = showFavoritesOnly
    ? baseFiltered.filter(p => favorites.has(p.id))
    : baseFiltered;

  return (
    <>
      <Helmet>
        <title>Nos Biens à la Vente | Investissement Immobilier | G Division</title>
        <meta name="description" content="Découvrez nos biens immobiliers à la vente : appartements, maisons et terrains divisés avec des rendements attractifs. Opportunités d'investissement sélectionnées par G Division." />
        <meta name="keywords" content="investissement immobilier, bien à vendre, rendement locatif, division immobilière, appartement investissement" />
        <link rel="canonical" href="https://divisionimmobiliere.com/nos-biens" />
        
        <meta property="og:title" content="Biens à la Vente | G Division" />
        <meta property="og:description" content="Opportunités d'investissement immobilier avec rendements attractifs." />
        <meta property="og:url" content="https://divisionimmobiliere.com/nos-biens" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="pt-20" data-testid="properties-page">
        {/* Hero */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-[#0f1d2f]">
          {/* Background image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1920&q=80)` 
            }}
          />
          {/* Multi-layer overlay for readability + depth */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f1d2f] via-[#0f1d2f]/85 to-[#0f1d2f]/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f1d2f]/80 via-transparent to-transparent"></div>

          {/* Decorative glows */}
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#c9a227]/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-green-500/15 backdrop-blur-md border border-green-400/30 text-green-400 text-xs font-bold px-4 py-2 rounded-full mb-6 uppercase tracking-wider">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400"></span>
                </span>
                Opportunités Sélectionnées
              </span>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.05]">
                Nos Biens
                <span className="block bg-gradient-to-r from-[#c9a227] via-[#e6c14d] to-[#c9a227] bg-clip-text text-transparent">
                  Rentables
                </span>
                <span className="block text-3xl sm:text-4xl md:text-5xl mt-2 text-white/95">à la Vente</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-2xl">
                Des opportunités d'investissement issues de nos opérations de division. 
                <strong className="text-white"> Rendements optimisés</strong>, biens sélectionnés pour CGP et investisseurs.
              </p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="bg-white border-b sticky top-20 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
            <div className="flex flex-wrap items-center gap-2">
              {['all', 'Appartement', 'Maison', 'Terrain', 'Immeuble'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    filter === type
                      ? 'bg-[#1e3a5f] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {type === 'all' ? 'Tous les biens' : type}
                </button>
              ))}
              <div className="ml-auto">
                <button
                  onClick={() => setShowFavoritesOnly((v) => !v)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    showFavoritesOnly
                      ? 'bg-rose-500 text-white shadow-md shadow-rose-500/30'
                      : 'bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200'
                  }`}
                  data-testid="favorites-filter-btn"
                >
                  <Heart size={14} className={showFavoritesOnly ? 'fill-white' : 'fill-rose-400'} />
                  {showFavoritesOnly ? 'Tous les biens' : `Mes favoris (${favorites.size})`}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Properties Grid */}
        <section className="py-12 md:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {/* Non-exhaustive notice */}
            <div className="mb-8 md:mb-10 bg-gradient-to-r from-[#1e3a5f]/5 to-[#c9a227]/5 border border-[#c9a227]/20 rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-[#c9a227]/15 rounded-xl flex items-center justify-center">
                <Phone className="text-[#c9a227]" size={22} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[#1e3a5f] mb-1">
                  Liste non exhaustive
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Pour une demande précise (secteur, typologie, budget, rendement cible...), <Link to="/contact" className="text-[#c9a227] font-semibold hover:underline">prenez contact avec nous</Link> — nous avons d'autres opportunités hors-marché à vous proposer.
                </p>
              </div>
              <a 
                href="https://wa.me/33645040975?text=Bonjour, je recherche un bien avec des critères spécifiques. Pouvez-vous me proposer vos opportunités hors-marché ?"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-2 bg-[#c9a227] hover:bg-[#b08d22] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg"
                data-testid="bespoke-request-cta"
              >
                <Phone size={16} />
                Demande sur mesure
              </a>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] mx-auto"></div>
                <p className="mt-4 text-gray-500">Chargement des biens...</p>
              </div>
            ) : filteredProperties.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl">
                <Home className="mx-auto text-gray-300 mb-4" size={64} />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  Aucun bien disponible pour le moment
                </h3>
                <p className="text-gray-500 mb-6">
                  De nouvelles opportunités arrivent bientôt !
                </p>
                <Link to="/contact">
                  <Button className="bg-[#1e3a5f] text-white hover:bg-[#2d4a6f]">
                    Être alerté des nouveaux biens
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filteredProperties.map((property) => {
                  const TypeIcon = getTypeIcon(property.property_type);
                  return (
                    <Link
                      key={property.id}
                      to={`/nos-biens/${property.id}`}
                      className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group block"
                      data-testid={`property-card-${property.id}`}
                    >
                      {/* Image */}
                      <div className="relative h-56 overflow-hidden">
                        {property.images && property.images.length > 0 ? (
                          <img 
                            src={property.images[0]} 
                            alt={property.title}
                            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${property.status === 'Vendu' ? 'grayscale' : ''}`}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#1e3a5f] to-[#0f1d2f] flex items-center justify-center">
                            <TypeIcon className="text-white/30" size={64} />
                          </div>
                        )}
                        
                        {/* VENDU Overlay */}
                        {property.status === 'Vendu' && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <div className="bg-red-600 text-white text-2xl font-black px-8 py-3 rounded-lg transform -rotate-12 shadow-lg uppercase tracking-wider">
                              VENDU
                            </div>
                          </div>
                        )}
                        
                        {/* Status Badge (for non-vendu) */}
                        {property.status !== 'Vendu' && (
                          <div className={`absolute top-4 left-4 ${getStatusColor(property.status)} text-white text-sm font-bold px-4 py-2 rounded-lg shadow-lg`}>
                            {property.status === 'Disponible' ? '✓ DISPONIBLE' : property.status.toUpperCase()}
                          </div>
                        )}
                        
                        {/* Favorite heart */}
                        <button
                          onClick={(e) => toggleFavorite(property.id, e)}
                          className={`absolute bottom-4 right-4 w-10 h-10 rounded-full backdrop-blur-md flex items-center justify-center transition-all duration-300 shadow-lg z-20 ${
                            favorites.has(property.id)
                              ? 'bg-rose-500 text-white scale-100 hover:scale-110'
                              : 'bg-white/80 text-gray-600 hover:bg-rose-50 hover:text-rose-500 hover:scale-110'
                          }`}
                          aria-label={favorites.has(property.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                          data-testid={`favorite-btn-${property.id}`}
                        >
                          <Heart size={18} className={favorites.has(property.id) ? 'fill-white' : ''} />
                        </button>

                        {/* ROI Badge — visible aussi pour les biens vendus */}
                        {property.roi && (
                          <div className="absolute top-4 right-4 bg-[#c9a227] text-white text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1 shadow-lg z-10">
                            <TrendingUp size={14} />
                            {property.roi}% ROI
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="p-6">
                        {/* Type */}
                        <div className="flex items-center gap-2 text-[#c9a227] text-sm font-medium mb-2">
                          <TypeIcon size={16} />
                          {property.property_type}
                        </div>
                        
                        {/* Title */}
                        <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1e3a5f] mb-2 line-clamp-2 group-hover:text-[#c9a227] transition-colors">
                          {property.title}
                        </h3>
                        
                        {/* Location */}
                        <div className="flex items-center gap-1 text-gray-500 text-sm mb-4">
                          <MapPin size={14} />
                          {property.location}
                        </div>
                        
                        {/* Stats */}
                        <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100">
                          <div className="flex items-center gap-1 text-gray-600">
                            <Maximize size={16} />
                            <span className="text-sm font-medium">{property.surface} m²</span>
                          </div>
                          {property.rooms && (
                            <div className="flex items-center gap-1 text-gray-600">
                              <Home size={16} />
                              <span className="text-sm font-medium">{property.rooms} pièces</span>
                            </div>
                          )}
                        </div>

                        {/* Investment Metrics */}
                        {property.estimated_rent && property.status !== 'Vendu' && (
                          <div className="bg-green-50 border border-green-100 rounded-xl p-3 mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-green-700">
                              <Banknote size={16} />
                              <span className="text-xs font-medium uppercase">Loyer estimé</span>
                            </div>
                            <span className="font-bold text-green-700">{property.estimated_rent}€/mois</span>
                          </div>
                        )}
                        
                        {/* Price + CTA */}
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Prix</p>
                            <p className="text-2xl font-bold text-[#1e3a5f]">
                              {formatPrice(property.price)}
                            </p>
                          </div>
                          {property.status !== 'Vendu' && (
                            <span
                              role="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.open(
                                  `https://wa.me/33645040975?text=Bonjour, je suis intéressé par le bien "${property.title}" à ${property.location}`,
                                  '_blank',
                                  'noopener,noreferrer'
                                );
                              }}
                              className="inline-flex items-center bg-green-500 hover:bg-green-600 text-white rounded-xl px-4 py-2 text-sm font-medium cursor-pointer transition-colors"
                              data-testid={`whatsapp-btn-${property.id}`}
                            >
                              <Phone size={16} className="mr-2" />
                              Contact
                            </span>
                          )}
                        </div>

                        {/* See details hint */}
                        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                          <span className="text-gray-500">Voir le détail</span>
                          <ArrowRight size={16} className="text-[#c9a227] group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-white border-t">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <span className="text-[#c9a227] text-sm font-bold uppercase tracking-wider">Notre Promesse</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mt-2 mb-3">
                Pourquoi Investir avec G Division&nbsp;?
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Nous sélectionnons et préparons des biens issus de nos opérations de division pour offrir aux investisseurs des rendements supérieurs au marché.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl p-6">
                <div className="w-12 h-12 bg-[#c9a227]/10 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="text-[#c9a227]" size={24} />
                </div>
                <h3 className="font-semibold text-[#1e3a5f] text-lg mb-2">Rendements Optimisés</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Nos biens issus de divisions affichent des rendements bruts supérieurs à 6%, bien au-dessus du marché classique.
                </p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl p-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
                <h3 className="font-semibold text-[#1e3a5f] text-lg mb-2">Clé en Main</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Accompagnement complet&nbsp;: rénovation, mise aux normes, et mise en location pour un investissement passif.
                </p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <PiggyBank className="text-blue-600" size={24} />
                </div>
                <h3 className="font-semibold text-[#1e3a5f] text-lg mb-2">Cashflow Positif</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Des biens qui s'autofinancent dès le premier mois. Simulez le cashflow directement sur chaque fiche bien.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-[#1e3a5f]">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-white mb-4">
              Vous êtes CGP ou Investisseur ?
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Recevez nos opportunités en avant-première. 
              Des biens sélectionnés avec des rendements optimisés.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-[#c9a227] hover:bg-[#b08d22] text-white px-8 py-6 text-lg font-semibold rounded-xl">
                  Devenir Partenaire
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="https://wa.me/33645040975" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-xl">
                  <Phone className="mr-2" size={20} />
                  WhatsApp Direct
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Properties;
